import React from "react"
import {
    ImageCard,
    LinkButton,
    Orientation,
    TextMedium,
    componentStyles,
} from "@mallardbay/lib-react-components"
import type { ComponentProps } from "react"

import { useIsMobile } from "~components/shared/todo-lib-react-components/hooks/use-is-mobile"
import COPY_CONSTANTS from "~config/copy-constants"
import type { BlogEntryType } from "~utils/blog-helpers"
import { getPathForBlogEntry } from "~utils/blog-helpers"
import { formatBlogEntryDate } from "~utils/date-helpers"

interface Props {
    readonly blogEntry: BlogEntryType
    readonly orientation?: Orientation
    readonly shouldShowBorder?: boolean
}

export default function BlogEntryPreview({
    blogEntry,
    orientation,
    shouldShowBorder,
}: Props) {
    // If we have no data, there's nothing to show
    const blogEntryData = blogEntry?.data
    const imageCardProps = useCardImageProps({ blogEntry, shouldShowBorder })
    const styles = useStyles()

    if (!imageCardProps) return null

    return (
        <ImageCard
            {...imageCardProps}
            orientation={orientation ?? imageCardProps.orientation}
        >
            <TextMedium>{blogEntryData?.shortDescription}</TextMedium>
            <LinkButton
                style={styles.link}
                colorScheme="brand"
                href={imageCardProps.linkTo}
            >
                {COPY_CONSTANTS.READ} →
            </LinkButton>
        </ImageCard>
    )
}

function useCardImageProps({
    blogEntry,
    shouldShowBorder,
}: {
    blogEntry: BlogEntryType
    shouldShowBorder?: boolean
}) {
    const isMobile = useIsMobile()

    const blogEntryData = blogEntry?.data
    if (!blogEntryData) return null
    const { title, shortDescription, coverImage, postDate } = blogEntryData

    if (!coverImage || !title || !shortDescription || !postDate) {
        // If we don't have the bare min to show a good entry preview,
        // bail
        return null
    }

    const orientation = isMobile ? Orientation.Vertical : Orientation.Horizontal
    // Parse and format the date
    const formattedDate = formatBlogEntryDate(postDate)
    const linkTo = getPathForBlogEntry(blogEntry) ?? "/"

    return {
        orientation,
        isLoading: false,
        subtitle: formattedDate,
        title: title,
        headerImageSrc: coverImage,
        linkTo,
        style: {
            ...(shouldShowBorder ? {} : { border: "none" }),
        },
    } as const satisfies ComponentProps<typeof ImageCard>
}

function useStyles() {
    return componentStyles({ link: { float: "right" } })
}
